import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";

function NotFoundComponent() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const t = window.setTimeout(() => {
      window.location.assign("/");
    }, 5000);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved. Redirecting you to the homepage in 5 seconds…
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: ({ matches }) => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "PageOne Lawyers, SEO Company for Law Firms" },
      { name: "description", content: "Specialist SEO agency for law firms. Personal injury, DUI, family, criminal defense and more. Proven rankings, real cases, ethical SEO." },
      { name: "author", content: "PageOne Lawyers" },
      { property: "og:site_name", content: "PageOne Lawyers" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "theme-color", content: "#0a1628" },
      { property: "og:title", content: "PageOne Lawyers, SEO Company for Law Firms" },
      { name: "twitter:title", content: "PageOne Lawyers, SEO Company for Law Firms" },
      { property: "og:description", content: "Specialist SEO agency for law firms. Personal injury, DUI, family, criminal defense and more. Proven rankings, real cases, ethical SEO." },
      { name: "twitter:description", content: "Specialist SEO agency for law firms. Personal injury, DUI, family, criminal defense and more. Proven rankings, real cases, ethical SEO." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/3c1be7d1-98d4-45b0-a3d8-0567fe03da09" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/3c1be7d1-98d4-45b0-a3d8-0567fe03da09" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "apple-touch-icon", href: "/favicon.svg" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "preload", as: "style", href: "https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap" },
    ],
    scripts: matches.some((match) => ["/best-law-firm-seo-companies", "/blog/law-firm-performance-metrics", "/blog/social-media-scheduling-tools-for-law-firms", "/blog/how-to-use-technology-to-improve-legal-marketing", "/blog/how-to-measure-roi-on-law-firm-marketing", "/blog/how-to-generate-leads-for-law-firms", "/blog/how-law-firms-get-found-on-chatgpt", "/practice-areas/criminal-defense-lawyer-seo", "/practice-areas/estate-planning-lawyer-seo", "/practice-areas/personal-injury-lawyer-seo", "/practice-areas/bankruptcy-lawyer-seo", "/practice-areas/family-law-lawyer-seo", "/practice-areas/corporate-business-lawyer-seo", "/practice-areas/immigration-lawyer-seo", "/practice-areas/dui-dwi-lawyer-seo", "/practice-areas/employment-law-lawyer-seo", "/practice-areas/medical-malpractice-lawyer-seo", "/practice-areas/workers-compensation-lawyer-seo", "/practice-areas/real-estate-law-lawyer-seo", "/practice-areas/tax-law-lawyer-seo"].includes(match.pathname.replace(/\/$/, ""))) ? [] : [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "PageOne Lawyers",
          url: "https://pageonelawyers.com/",
          description: "SEO company for law firms across every practice area.",
          sameAs: [],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  return (
    <html lang={pathname.startsWith("/uk/") ? "en-GB" : "en"}>
      <head>
        <script
          dangerouslySetInnerHTML={{
            __html:
              "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=window.gtag||gtag;gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied',functionality_storage:'granted',security_storage:'granted',wait_for_update:500});gtag('set','ads_data_redaction',true);gtag('set','url_passthrough',true);(function(w,d,s,l,i){if(w.__pageOneGtmLoaded)return;w.__pageOneGtmLoaded=true;w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-MLFXHCXB');",
          }}
        />
        <script
          dangerouslySetInnerHTML={{
            __html:
              "window.addEventListener('click',function(e){if(e.button!==0||e.metaKey||e.ctrlKey||e.shiftKey||e.altKey)return;var t=e.target;if(!t||!t.closest)return;var a=t.closest('a[href]');if(!a||a.target&&a.target!=='_self'||a.hasAttribute('download'))return;var h=a.getAttribute('href');if(!h||h.charAt(0)==='#'||/^(mailto|tel|sms|javascript):/i.test(h))return;var u=new URL(a.href,location.href);if(u.origin!==location.origin)return;if(u.pathname===location.pathname&&u.search===location.search&&u.hash)return;e.preventDefault();setTimeout(function(){location.assign(u.href);},150);},false);",
          }}
        />
        <script
          dangerouslySetInnerHTML={{
            __html:
              "(function(){var h='https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap';function add(){if(document.querySelector('link[rel=\"stylesheet\"][href=\"'+h+'\"]'))return;var l=document.createElement('link');l.rel='stylesheet';l.href=h;document.head.appendChild(l);}if(document.readyState!=='loading'){add();}else{document.addEventListener('DOMContentLoaded',add);}})();",
          }}
        />
        <script
          dangerouslySetInnerHTML={{
            __html:
              "(function(){function clean(){try{var u=new URL(location.href);var p=u.searchParams;if(!p.has('_gl'))return;p.delete('_gl');var q=p.toString();history.replaceState(history.state,'',u.pathname+(q?'?'+q:'')+u.hash);}catch(e){}}setTimeout(clean,1200);window.addEventListener('load',function(){setTimeout(clean,1200);});})();",
          }}
        />

        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-MLFXHCXB"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
    </QueryClientProvider>
  );
}
